import React from 'react';
import path from 'path';

import { analytics } from 'core/analytics';
import { routerStore } from 'core/routing';

import { ImageViewer } from 'libs/rendering/images/viewer';
import { PDFViewer } from 'libs/rendering/pdf/viewer';
import { TextViewer } from 'libs/rendering/text/viewer';
import { VideoViewer } from 'libs/rendering/video/viewer';
import { NoViewer } from 'libs/rendering/noviewer/viewer';
import { LoadingViewer } from 'libs/rendering/loading/viewer';

import { loader } from 'legacyComponents/hoc/loader';

import { Loading as LoadingNew } from 'libs/rendering/gltf/components/loading';

import { conf as formatData } from 'apps/repository/formats.yaml';

// FIXME - review this file

const GltfViewer = GLTF_URL_BASE
  ? loader({
      getComponent: async () => {
        const viewerModule = await import(
          /* webpackChunkName: 'gltf-viewer' */ 'libs/rendering/gltf/viewer'
        );
        return viewerModule.default;
      },
      Fallback(props) {
        // eslint-disable-next-line react/prop-types
        return <LoadingNew text={props.forceLoadingText} />;
      },
    })
  : null;

export const Viewers = {
  ImageViewer,
  PDFViewer,
  GltfViewer,
  TextViewer,
  MarkdownViewer: TextViewer,
  VideoViewer,
  NoViewer,
};

export const Previewers = {
  ImageViewer: 'image',
  GltfViewer: 'looping-video',
  PDFViewer: 'pdf',
  TextViewer: 'text',
  MarkdownViewer: 'wip',
  VideoViewer: 'video',
};

export const ambiguous_mimes = new Set([
  '',
  'text/xml',
  'text/plain',
  'application/octet-stream',
]);

export const mimeViewers = {};
export const extViewers = {};

export const initFormats = () => {
  // detect duplicates for a mime or extension
  const { viewers } = formatData;
  // eslint-disable-next-line
  for (const [viewerName, viewer] of Object.entries(viewers)) {
    // eslint-disable-next-line
    for (const [formatName, format] of Object.entries(viewer)) {
      format.viewer = viewerName;
      format.name = formatName;

      if (format.extensions) {
        // eslint-disable-next-line
        for (const ext of format.extensions) {
          if (!extViewers[ext]) {
            extViewers[ext] = [];
          }
          extViewers[ext].push(format);
        }
      }

      if (format.mimeTypes) {
        // eslint-disable-next-line
        for (const mime of format.mimeTypes) {
          if (!mimeViewers[mime]) {
            mimeViewers[mime] = [];
          }
          mimeViewers[mime].push(format);
        }
      }
    }
  }
};

initFormats();

const getMatchingFormats = (mime = '', extension) => {
  /* Preference using viewers by mimetype if identified and present in our
   mimetype map. Otherwise fall back to identifying the format/application by
   extension. */

  let formatList;
  if (ambiguous_mimes.has(mime) && extViewers[extension.toLowerCase()]) {
    // if the mimetype is ambiguous, try and use the extension
    formatList = extViewers[extension.toLowerCase()];
  } else {
    formatList = mimeViewers[mime];
    if (!formatList) {
      // if server identified mimeType is not known on the client use the ext
      formatList = extViewers[extension.toLowerCase()];
    }
  }

  return formatList || [];
};

export const getFormatObject = file => {
  if (!file) {
    const formatObject = { viewer: 'Loading' };
    const extension = null;
    return { formatObject, extension };
  }

  const name = file.filename;
  if (!name) {
    const formatObject = { viewer: 'Loading' };
    const extension = null;
    return { formatObject, extension };
  }
  const fileNameArray = name.split('.');
  if (fileNameArray.length < 1) {
    const formatObject = { viewer: 'Loading' };
    const extension = null;
    return { formatObject, extension };
  }

  let extension = fileNameArray[fileNameArray.length - 1]
    .replace('.', '')
    .toLowerCase();

  /*
    Some users upload files with an "extra" extension for managing part numbering,
     like [someCadPart.ptc.1]. Mostly due to automated scripts in their internal
     CAD versioning workflow, so it may be hard for them to stop using it.

    Here we try to skip that "extra" extension and keep the "real" one.
  */
  if (fileNameArray.length > 2) {
    const altExtension = fileNameArray[fileNameArray.length - 2]
      .replace('.', '')
      .toLowerCase();

    if (
      extViewers &&
      altExtension &&
      !extViewers[extension] &&
      extViewers[altExtension]
    ) {
      // ignore "extra" extension, keep the other one
      extension = altExtension;
    }
  }

  const formats = getMatchingFormats(file.mimeType, extension);

  let formatObject = null;
  if (formats.length > 0) {
    // - pdf files can be displayed by both the PDFViewer or the GltfViewer
    // (if they contain a 3D model), so we first check all formats for the
    // PDFViewer and test if conversionGltfjson !== 'SUCCESS', and then we
    // assume we should use the PDFViewer
    // - this can either mean conversionGltfjson === 'ERROR' for some reason
    // and we need to fall back to using the PDFViewer, or it means
    // conversionGltfjson === 'PDF' because we determined that this is a
    // 2D pdf file correctly, or it is conversionGltfjson.status === 'PENDING'
    // and we don't know yet if this is a 2D pdf or 3D pdf
    // eslint-disable-next-line
    for (const format of formats) {
      if (
        format.viewer === 'PDFViewer' &&
        file.conversionGltfjson &&
        file.conversionGltfjson.status !== 'SUCCESS'
      ) {
        formatObject = format;
        break;
      }
    }

    // - if the above loop does not determine that the viewer should be the
    // PDFViewer, then we check the formats for the GltfViewer and prioritize
    // that
    // - so even if conversionGltfjson === 'ERROR' we should still pick that
    // formatObject to return, the GltfViewer will then handle the ERROR case
    // and display an error message
    if (!formatObject) {
      // eslint-disable-next-line
      for (const format of formats) {
        if (format.viewer === 'GltfViewer') {
          formatObject = format;
          break;
        }
      }
    }

    // - if we neither picked pdf or gltf, then use the first option of
    // what getMatchingFormats return
    // - if we didn't do this then formatObject would end up being null
    // and we'd fall back to NoViewer below
    if (!formatObject) {
      [formatObject] = formats;
    }
  }

  // - at this point if formatObject is still null, we return NoViewer
  // which will display a message that this format is not supported yet
  if (!formatObject) {
    formatObject = { viewer: 'NoViewer' };
  }

  return { formatObject, extension };
};

export const getViewer = formatObject => {
  if (formatObject?.viewer === 'Loading') {
    return LoadingViewer;
  }

  if (formatObject?.viewer && Viewers[formatObject.viewer]) {
    return Viewers[formatObject.viewer];
  }

  return NoViewer;
};

export const getPreviewer = formatObject =>
  formatObject.viewer && Previewers[formatObject.viewer]
    ? Previewers[formatObject.viewer]
    : 'default';

// Available viewers list ['ImageViewer', 'TextViewer', 'VideoViewer', 'PDFViewer', 'GltfViewer']
export const getMimeTypes = viewer => {
  const { viewers } = formatData;
  if (!Object.keys(viewers).includes(viewer)) return [];
  const selectedViewer = viewers[viewer];
  const selectedViewerKeys = Object.keys(selectedViewer);

  let output = '';

  selectedViewerKeys.forEach(key => {
    if (Array.isArray(selectedViewer[key]?.mimeTypes)) {
      selectedViewer[key].mimeTypes.forEach(value => {
        output = output.concat(output === '' ? '' : ',', value);
      });
    }
  });

  return output;
};

export const getExtensions = viewer => {
  const { viewers } = formatData;
  const selectedViewer = viewers[viewer] || [];
  return Array.from(
    new Set(
      Object.values(selectedViewer)
        .map(({ extensions }) => extensions)
        .flat()
        .filter(extensions => !!extensions),
    ),
  );
};

export const normalizeURI = link => {
  let normalizedLink = '';
  try {
    normalizedLink = encodeURI(decodeURI(link));
  } catch (error) {
    const scopingFunction = scope => {
      scope.setExtra('link', link);
    };
    analytics.logError(error, scopingFunction);
  }

  return normalizedLink;
};

export const isViewerURL = link => {
  const normalizedLink = normalizeURI(link);
  let matches = null;
  try {
    matches = routerStore.router.matchUrl(normalizedLink);
  } catch (error) {
    const scopingFunction = scope => {
      scope.setExtra('link', link);
    };
    analytics.logError(error, scopingFunction);
  }
  return !!(
    matches &&
    matches.name &&
    (matches.name.endsWith('.file') ||
      (matches.name === 'viewer' && matches.params.filename))
  );
};

export const viewerURLToParams = link => {
  const normalizedLink = normalizeURI(link);
  let matches = null;
  try {
    matches = routerStore.router.matchUrl(normalizedLink);
  } catch (error) {
    const scopingFunction = scope => {
      scope.setExtra('link', link);
    };
    analytics.logError(error, scopingFunction);
  }
  if (!matches) return null;
  let type;
  let contextPath;
  if (matches.name === 'viewer') {
    type = 'Upload';
    matches.params.fileID = decodeURI(matches.params.fileID);
  } else if (matches.name.endsWith('.file')) {
    type = matches.params.contributionId ? 'Versioned Embed' : 'Embed';
    matches.params.basepath = decodeURI(matches.params.basepath);
    contextPath = `${matches.params.initiative || matches.params.profile}/${
      matches.params.slug
    }`;
  } else {
    return null;
  }
  return {
    type,
    contextPath,
    ...matches,
  };
};

export const viewerURLtoPreviewer = (link, opts = { thumbnail: false }) => {
  const { thumbnail } = opts;
  const typeString = thumbnail ? 'thumb' : 'preview';
  const matches = viewerURLToParams(link);

  if (!matches) return '';
  if (matches.name === 'viewer') {
    return `${MAIN_URL}/${typeString}/${matches.params.fileID}`;
  }
  if (matches.name.endsWith('.file')) {
    return `${MAIN_URL}/${typeString}${matches.path}`;
  }
  return '';
};

export const viewerURLtoFilename = link => {
  const matches = viewerURLToParams(link);
  if (!matches) return '';
  if (matches.name === 'viewer') {
    return matches.params.filename || '';
  }
  if (matches.name.endsWith('.file')) {
    return path.basename(matches.params.basepath);
  }
  return '';
};

export const viewerURLtoPreviewInfo = link => {
  const infoTemplate = { image: {}, file: {}, video: null };
  const matches = viewerURLToParams(link);
  if (!matches) return infoTemplate;
  if (!(matches.name === 'viewer' || matches.name.endsWith('.file'))) {
    return infoTemplate;
  }
  const filename =
    matches.params.filename ||
    (matches.params.basepath && path.basename(matches.params.basepath));
  if (!filename) {
    infoTemplate.image.url = viewerURLtoPreviewer(link, { thumbnail: true });
    return infoTemplate;
  }

  const formatObject = getFormatObject({});

  const previewType = getPreviewer(formatObject);
  switch (previewType) {
    case 'image': {
      infoTemplate.image.url = viewerURLtoPreviewer(link);
      break;
    }
    case 'looping-video': {
      infoTemplate.image.url = viewerURLtoPreviewer(link, { thumbnail: true });
      infoTemplate.video = {
        url: viewerURLtoPreviewer(link),
        width: 800,
        height: 600,
      };
      break;
    }
    case 'video': {
      infoTemplate.image.url = viewerURLtoPreviewer(link, { thumbnail: true });
      infoTemplate.video = {
        url: viewerURLtoPreviewer(link),
        width: 800,
        height: 'auto',
      };
      break;
    }
    case 'pdf': {
      infoTemplate.image.url = viewerURLtoPreviewer(link, { thumbnail: true });
      break;
    }
    case 'text': {
      infoTemplate.file.url = viewerURLtoPreviewer(link, { thumbnail: true });
      break;
    }
    default:
      break;
  }
  return infoTemplate;
};
